/**
 * Style sheets — the file a style *is*.
 *
 * A style is not a named bundle of values living inside a scene; it is an
 * ordinary Canopy sheet (`{version, label?, declarations}`) stored as its own
 * asset. Applying one is referencing its URL from `PropertiesData.sheets`
 * (see `style-actions.ts`), which is why nothing here resolves, matches, or
 * bakes anything: this module only builds and reads the file.
 *
 * What it does own is the **shape** question — given a property name and a
 * value, what declaration should carry it? Published theme names answer from
 * `scenedata/theme-vocabulary.ts` (`structureSrc` is a `string` of kind
 * `material`, not a plain string; `accentColor` is a `color`, not the plain
 * string its value would suggest); everything else is inferred from the value,
 * because a style may set bespoke names too.
 */

import type {
    PropertyDeclaration,
    PropertySheet,
} from '../scenedata/behaviors/propertiesdata';
import { PROPERTY_SHEET_VERSION } from '../scenedata/behaviors/propertiesdata';
import type { PropertyValue } from '../behaviors/tracebehavior';
import type { SceneObject } from '../core/SceneObject';
import { Properties } from '../behaviors/properties';
import {
    THEME_PROPERTY_SHAPES,
    THEME_DECLARATION_INHERITS,
} from '../scenedata/theme-vocabulary';
import type { DeclarationShape } from '../scenedata/theme-vocabulary';

const HEX_COLOR_RE = /^#[0-9A-Fa-f]{6}([0-9A-Fa-f]{2})?$/;

/**
 * Whether a value is assignable to a declaration of the given type.
 *
 * Mirrors the resolver's own check — `color` is any string there too, because
 * hex validity is the consumer's concern and `''` is a meaningful "don't
 * drive" value rather than a malformed color.
 */
export function valueMatchesType(type: PropertyDeclaration['type'], value: PropertyValue): boolean {
    switch (type) {
        case 'number': return typeof value === 'number' && Number.isFinite(value);
        case 'boolean': return typeof value === 'boolean';
        case 'string':
        case 'color': return typeof value === 'string';
        case 'vec3': return Array.isArray(value) && value.length === 3 && value.every(n => typeof n === 'number');
        case 'vec4': return Array.isArray(value) && value.length === 4 && value.every(n => typeof n === 'number');
        default: return false;
    }
}

/** The shape a value implies when its name carries no vocabulary. */
function inferShape(value: PropertyValue): DeclarationShape | null {
    if (typeof value === 'number') return { type: 'number' };
    if (typeof value === 'boolean') return { type: 'boolean' };
    if (typeof value === 'string') return HEX_COLOR_RE.test(value) ? { type: 'color' } : { type: 'string' };
    if (Array.isArray(value) && value.length === 3) return { type: 'vec3' };
    if (Array.isArray(value) && value.length === 4) return { type: 'vec4' };
    return null;
}

/** The declaration shape for a property name, from the role vocabulary or inferred. */
export function shapeForProperty(name: string, value: PropertyValue): DeclarationShape | null {
    return THEME_PROPERTY_SHAPES.get(name) ?? inferShape(value);
}

/**
 * The declaration that carries `value` under `name`, or null when no shape can
 * be decided (an unrepresentable value) or the value contradicts the name's
 * vocabulary (`structureRoughness: "gold"`).
 *
 * Null is a skip signal, never a fallback: guessing a shape here would write a
 * declaration whose type silently disagrees with every consumer bound to it.
 *
 * **`inherits: false` is load-bearing.** Canopy's default is that the
 * *outermost* provider declaring a name supplies the value (CANOPY_CORE §5.2 —
 * the flag exists so an outer scope can parameterize an inner template). Under
 * that default a style applied to a branch of an already-themed subtree is a
 * silent no-op: the scene root's style keeps winning, however much nearer the
 * branch's is. Styles want the opposite — the nearest style is the one in
 * effect — so every declaration a style writes pins itself. Nothing else
 * changes: with only one provider declaring a name, both flags resolve
 * identically.
 */
export function declarationForValue(name: string, value: PropertyValue): PropertyDeclaration | null {
    const shape = shapeForProperty(name, value);
    if (!shape || !valueMatchesType(shape.type, value)) return null;
    return { name, ...shape, inherits: THEME_DECLARATION_INHERITS, value } as PropertyDeclaration;
}

/** What {@link declarationsFromValues} produced, and what it could not. */
export interface DeclarationsFromValues {
    declarations: PropertyDeclaration[];
    /** Property names skipped because no declaration could carry their value. */
    skipped: string[];
}

/**
 * Project a name→value map onto declarations, in insertion order.
 *
 * This is the one conversion a style ever needs: the value-bundle shape (a
 * legacy style, a captured theme, an AI-emitted palette) becomes the sheet
 * shape. Unrepresentable entries are reported rather than dropped silently —
 * a style that quietly loses a role reads as an engine bug.
 */
export function declarationsFromValues(values: Record<string, PropertyValue>): DeclarationsFromValues {
    const declarations: PropertyDeclaration[] = [];
    const skipped: string[] = [];
    for (const [name, value] of Object.entries(values)) {
        const declaration = declarationForValue(name, value);
        if (declaration) declarations.push(declaration);
        else skipped.push(name);
    }
    return { declarations, skipped };
}

/**
 * Every sheet loaded across `objects`, keyed by the URL that referenced it.
 *
 * The input for publish-flatten (`export/flattenSheetRefs.ts`). Reading the
 * *loaded* sheets rather than re-fetching is the point: the live scene already
 * resolved every reference, including ones served from a pack, so flattening
 * cannot disagree with what the editor was showing.
 */
export function collectLoadedSheets(objects: Iterable<SceneObject>): Map<string, PropertySheet> {
    const sheets = new Map<string, PropertySheet>();
    for (const object of objects) {
        const properties = object.getBehavior(Properties);
        if (!properties) continue;
        for (const url of properties.getSheetUrls()) {
            if (!url || sheets.has(url)) continue;
            const sheet = properties.getLoadedSheet(url);
            if (sheet) sheets.set(url, sheet);
        }
    }
    return sheets;
}

/**
 * Build a style sheet file from a name→value map.
 *
 * `skipped` rides alongside the sheet rather than on it — the sheet is written
 * to disk verbatim, and an extra diagnostic key would end up in the file.
 */
export function createStyleSheet(
    values: Record<string, PropertyValue>,
    label?: string,
): { sheet: PropertySheet; skipped: string[] } {
    const { declarations, skipped } = declarationsFromValues(values);
    return {
        sheet: {
            version: PROPERTY_SHEET_VERSION,
            ...(label ? { label } : {}),
            declarations,
        },
        skipped,
    };
}
